import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { HeroSearch } from "@/components/HeroSearch";
import { ProductCard } from "@/components/ProductCard";
import { UnifiedFilters } from "@/components/UnifiedFilters";
import { useApp } from "@/contexts/AppContext";
import { Skeleton } from "@/components/ui/skeleton";
import { LocationDialog } from "@/components/LocationDialog";
import { trackSearch } from "@/lib/analytics";
import { useConsumerAuth } from "@/contexts/ConsumerAuthContext";
import { Link } from "react-router-dom";
import { Heart } from "lucide-react";
import { MapPin, SlidersHorizontal } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const DEFAULT_FILTERS = {
  category: "",
  subcategory: "",
  size: "",
  type: "",
  brand: "",
  effects: [],
  thc_min: 0,
  price_max: 200,
  max_distance_mi: 250,
  in_stock_only: false,
  pickup: false,
  delivery: false,
};

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const urlEffect = params.get("effect") || "";
  const urlCategory = params.get("category") || "";
  const urlSubcategory = params.get("subcategory") || "";
  const urlSize = params.get("size") || "";
  const urlThcMin = Number(params.get("thc_min") || 0) || 0;
  const urlPriceMax = Number(params.get("price_max") || 0) || 0;
  const urlType = params.get("type") || "";
  const urlBrand = params.get("brand") || "";
  const urlSort = params.get("sort") || "";
  const urlInStock = params.get("in_stock_only") === "1";
  const { location } = useApp();
  const { user, favoriteCount } = useConsumerAuth();

  const [filters, setFilters] = useState(() => ({
    ...DEFAULT_FILTERS,
    effects: urlEffect ? [urlEffect] : [],
    category: urlCategory || "",
    subcategory: urlSubcategory || "",
    size: urlSize || "",
    type: urlType || "",
    brand: urlBrand || "",
    thc_min: urlThcMin || DEFAULT_FILTERS.thc_min,
    price_max: urlPriceMax || DEFAULT_FILTERS.price_max,
    in_stock_only: urlInStock,
  }));
  const [sort, setSort] = useState(urlSort || "relevance");
  const [results, setResults] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [locOpen, setLocOpen] = useState(false);
  const [brandOptions, setBrandOptions] = useState([]);

  // Fetch brand list once — scoped to the user's state if we know it, so
  // the dropdown never lists brands the user can't legally shop.
  useEffect(() => {
    let cancelled = false;
    const stateCode = (location?.state || "").toUpperCase();
    api
      .get("/brands", {
        params: { limit: 100, ...(stateCode ? { state: stateCode } : {}) },
      })
      .then((r) => !cancelled && setBrandOptions(r.data?.brands || []))
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [location?.state]);

  // Sync URL ?effect= → filters.effects when arriving from outside (e.g. MoodFinder)
  useEffect(() => {
    if (!urlEffect) return;
    setFilters((f) =>
      (f.effects || []).includes(urlEffect)
        ? f
        : { ...f, effects: [urlEffect] }
    );
  }, [urlEffect]);

  // Sync URL ?category= → filters.category (from Home category grid)
  useEffect(() => {
    if (!urlCategory) return;
    setFilters((f) => (f.category === urlCategory ? f : { ...f, category: urlCategory }));
  }, [urlCategory]);

  const apiParams = useMemo(() => {
    const p = { sort, limit: 60 };
    if (q) p.q = q;
    if (filters.effects && filters.effects.length) {
      p.effects = filters.effects.join(",");
      p.effect_match = "all"; // multi-chip AND-matches
    }
    if (filters.category) p.category = filters.category;
    if (filters.subcategory) p.subcategory = filters.subcategory;
    if (filters.size) p.size = filters.size;
    if (filters.type) p.type = filters.type;
    if (filters.brand) p.brand = filters.brand;
    if (filters.thc_min) p.thc_min = filters.thc_min;
    if (filters.price_max && filters.price_max < 200) p.price_max = filters.price_max;
    if (filters.in_stock_only) p.in_stock_only = true;
    if (filters.pickup) p.pickup = true;
    if (filters.delivery) p.delivery = true;
    if (filters.live_menu_only) p.live_menu_only = true;
    if (location) {
      p.lat = location.lat;
      p.lon = location.lon;
      if (filters.max_distance_mi && filters.max_distance_mi < 250)
        p.max_distance_mi = filters.max_distance_mi;
    }
    return p;
  }, [q, filters, sort, location]);

  useEffect(() => {
    let live = true;
    setLoading(true);
    // Operator-funnel: log the search query if there is one. Dedupe is
    // handled inside trackSearch; no-op for empty strings.
    if (q) trackSearch(q);
    // Clear stale results IMMEDIATELY so the user sees the filter take effect
    // even if the API call is slow. Without this, the previous filter's top
    // cards stay on screen until the new fetch resolves — which looks like
    // "filters aren't working" even though they are.
    setResults([]);
    setCount(0);
    api
      .get("/search", {
        params: { ...apiParams, _t: Date.now() },
        headers: {
          "Cache-Control": "no-cache",
          Pragma: "no-cache",
        },
      })
      .then((r) => {
        if (!live) return;
        setResults(r.data.results);
        setCount(r.data.count);
      })
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
  }, [apiParams]);

  return (
    <main className="container-narrow py-10">
      <div className="mb-8">
        <HeroSearch defaultValue={q} />
      </div>

      {/* Soft account nudge: only shows when a signed-out visitor has
          already racked up 3+ favorites on this device. Anyone with fewer
          saves never sees it, signed-in users never see it. */}
      {!user && favoriteCount >= 3 && (
        <div
          className="mb-6 rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-4 flex items-center gap-3"
          data-testid="search-account-nudge"
        >
          <Heart className="w-4 h-4 text-rose-300 fill-rose-300/40 shrink-0" />
          <div className="text-sm text-foreground/80 leading-relaxed flex-1">
            You have <strong className="text-foreground">{favoriteCount} saves</strong> on
            this device.{" "}
            <Link
              to="/account"
              data-testid="search-account-nudge-cta"
              className="text-emerald-300 hover:text-emerald-200 underline-offset-2 hover:underline"
            >
              Sign in to keep them safe across devices.
            </Link>
          </div>
        </div>
      )}

      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="eyebrow mb-1">Search results</div>
          <h1 className="font-serif text-3xl">
            {filters.effects && filters.effects.length ? (
              <>
                For{" "}
                <span className="italic gold capitalize">
                  {filters.effects.join(" + ")}
                </span>{" "}
                <span className="text-foreground/50 text-2xl">
                  {q ? `· ${q}` : ""}
                </span>
              </>
            ) : q ? (
              <>For "{q}"</>
            ) : (
              "Browse all products"
            )}
          </h1>
          <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground mt-2">
            <span>
              {loading
                ? "Searching…"
                : `${count} listing${count === 1 ? "" : "s"} found`}
            </span>
            <span className="text-muted-foreground/60">·</span>
            <button
              data-testid="search-change-location"
              onClick={() => setLocOpen(true)}
              className="pill hover:bg-foreground hover:text-background hover:border-foreground transition-colors"
            >
              <MapPin className="w-3 h-3" strokeWidth={1.7} />
              {location
                ? `${
                    location.label
                      ? location.label + " · "
                      : ""
                  }${location.city || "PA"}${location.zip ? ` ${location.zip}` : ""}`
                : "Set location"}
              <span className="text-muted-foreground ml-1">change</span>
            </button>
          </div>
        </div>
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger
            data-testid="search-sort"
            className="w-[200px] rounded-full bg-card"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="relevance">Relevance · sponsored</SelectItem>
            <SelectItem value="distance">Distance</SelectItem>
            <SelectItem value="price_asc">Price: low → high</SelectItem>
            <SelectItem value="price_desc">Price: high → low</SelectItem>
            <SelectItem value="thc_desc">Highest THC</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid lg:grid-cols-[280px_1fr] gap-8">
        {/* Desktop sidebar */}
        <aside className="hidden lg:block">
          <UnifiedFilters
            filters={filters}
            setFilters={setFilters}
            onReset={() => setFilters(DEFAULT_FILTERS)}
            showDistance={!!location}
            brandOptions={brandOptions}
            testIdPrefix="search-filter"
          />
        </aside>

        {/* Mobile filter sheet trigger */}
        <div className="lg:hidden mb-4">
          <Sheet>
            <SheetTrigger asChild>
              <Button
                data-testid="search-mobile-filters"
                variant="outline"
                className="rounded-full w-full h-11 bg-transparent border-border"
              >
                <SlidersHorizontal className="w-4 h-4 mr-2" />
                Filters
              </Button>
            </SheetTrigger>
            <SheetContent
              side="bottom"
              className="bg-card border-border max-h-[85vh] overflow-y-auto rounded-t-3xl"
            >
              <SheetTitle className="font-serif text-2xl mb-4">Filters</SheetTitle>
              <UnifiedFilters
                filters={filters}
                setFilters={setFilters}
                onReset={() => setFilters(DEFAULT_FILTERS)}
                showDistance={!!location}
                brandOptions={brandOptions}
                testIdPrefix="search-filter-mobile"
              />
            </SheetContent>
          </Sheet>
        </div>

        <div>
          {loading ? (
            <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-5">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="aspect-[3/4] rounded-2xl" />
              ))}
            </div>
          ) : results.length === 0 ? (
            <div className="border border-dashed border-border rounded-2xl p-16 text-center">
              <div className="font-serif text-2xl mb-2">No results</div>
              <p className="text-sm text-muted-foreground">
                Try removing some filters or searching another strain.
              </p>
            </div>
          ) : (
            <div
              key={JSON.stringify(apiParams)}
              className="grid sm:grid-cols-2 xl:grid-cols-3 gap-5"
            >
              {results.map((p, idx) => (
                <ProductCard key={`${p.id}-${idx}`} product={p} />
              ))}
            </div>
          )}
        </div>
      </div>
      <LocationDialog open={locOpen} onOpenChange={setLocOpen} />
    </main>
  );
}
